/*
  assets/gtender-context-bar.js — GTENDER-CONTEXT-BAR-NAV-05
  Owner: GTender product-level Context Bar destination projection only.
  Scope: /pages/gtender, /pages/gtender-dashboard, /pages/gtender-partner-dashboard.
  Boundary:
  - LittleGWebSession owns GTender product identity.
  - assets/gtender-dashboard-access.js owns guest / authenticated-Free dashboard projection.
  - Server-side GTender services remain the only Pro / Provider authorization authorities.
  - This runtime never fetches authorization state, infers roles, writes entitlement, or grants access.
*/
(function () {
  'use strict';

  var VERSION = 'GTENDER-CONTEXT-BAR-NAV-05';
  var ROOT_SELECTOR = '[data-context-bar][data-context-product="gtender"][data-gtender-surface]';
  var GLOBAL_KEY = '__GTENDER_CONTEXT_BAR_RUNTIME__';
  var ALLOWED_SURFACES = Object.freeze({ public: true, pro: true, provider: true });
  var mounted = new WeakSet();

  if (window[GLOBAL_KEY] && typeof window[GLOBAL_KEY].boot === 'function') {
    window[GLOBAL_KEY].boot(document);
    return;
  }

  function clean(value) {
    return String(value == null ? '' : value)
      .replace(/\s+/g, ' ')
      .trim();
  }

  function list(scope, selector) {
    var root = scope || document;
    var output = [];
    if (root.matches && root.matches(selector)) output.push(root);
    if (root.querySelectorAll) Array.prototype.push.apply(output, root.querySelectorAll(selector));
    return output;
  }

  function surfaceOf(root) {
    var surface = clean(root && root.getAttribute('data-gtender-surface')).toLowerCase();
    return ALLOWED_SURFACES[surface] ? surface : 'public';
  }

  function language() {
    return clean(document.documentElement && document.documentElement.lang).toLowerCase();
  }

  function copy() {
    var lang = language();
    var hans = lang === 'zh-cn' || lang === 'zh-hans' || lang === 'zh-sg';
    var zh = /^zh(?:-|$)/i.test(lang);
    if (hans) {
      return {
        dashboard: '工作台',
        dashboardMark: '工作台',
        tenders: '公开招标',
        tendersMark: '招标',
      };
    }
    if (zh) {
      return {
        dashboard: '工作台',
        dashboardMark: '工作台',
        tenders: '公開招標',
        tendersMark: '招標',
      };
    }
    return {
      dashboard: 'Dashboard',
      dashboardMark: 'Dash',
      tenders: 'Tenders',
      tendersMark: 'Tenders',
    };
  }

  function safeGtenderRoute(value, fallback) {
    var safeFallback = clean(fallback) || '/pages/gtender';
    var raw = clean(value) || safeFallback;
    try {
      var url = new URL(raw, window.location.origin);
      if (url.origin !== window.location.origin) return safeFallback;
      if (
        !/^\/(?:[a-z]{2}(?:-[a-z0-9]+)*\/)?pages\/gtender(?:-dashboard|-partner-dashboard)?(?:\/|$)/i.test(
          url.pathname
        )
      )
        return safeFallback;
      return url.pathname + url.search + url.hash;
    } catch (_error) {
      return safeFallback;
    }
  }

  function normalizeProductTitle(root) {
    var surface = surfaceOf(root);
    if (surface === 'provider') return;
    var titles = root.querySelectorAll('.context-bar__workspace-return');
    Array.prototype.forEach.call(titles, function (title) {
      title.textContent = 'GTender';
      title.removeAttribute('href');
      title.setAttribute('aria-label', 'GTender');
      title.setAttribute('data-gtender-product-title', 'true');
    });
  }

  function syncStarControl(root) {
    var star = root.querySelector('[data-gtender-header-bookmarks]');
    if (!star) return;
    var isPublic = surfaceOf(root) === 'public';
    star.hidden = !isPublic;
    star.setAttribute('aria-hidden', isPublic ? 'false' : 'true');
    if (!isPublic) {
      star.setAttribute('tabindex', '-1');
      star.removeAttribute('aria-expanded');
    } else {
      star.removeAttribute('tabindex');
      if (!star.hasAttribute('aria-expanded')) star.setAttribute('aria-expanded', 'false');
    }
  }

  function projectionFor(root) {
    const labels = copy();
    const isPro = surfaceOf(root) === 'pro';
    const label = isPro ? labels.tenders : labels.dashboard;
    const mark = isPro ? labels.tendersMark : labels.dashboardMark;
    if (!label) return null;

    const publicUrl = safeGtenderRoute(
      root.getAttribute('data-gtender-public-url'),
      '/pages/gtender?source=context-bar'
    );
    const dashboardUrl = safeGtenderRoute(
      root.getAttribute('data-gtender-dashboard-url') || root.getAttribute('data-gtender-pro-url'),
      '/pages/gtender-dashboard?source=context-bar'
    );

    return {
      kind: isPro ? 'public' : 'dashboard',
      href: isPro ? publicUrl : dashboardUrl,
      label: label,
      mark: mark,
    };
  }

  function project(root) {
    if (!(root instanceof HTMLElement)) return;
    var entry = root.querySelector('[data-gtender-workspace-entry]');
    if (!entry) return;

    const projection = projectionFor(root);
    if (!projection) return;
    var text = entry.querySelector('.context-bar__gtender-workspace-text');
    var mark = entry.querySelector('.context-bar__gtender-workspace-mark');

    entry.hidden = false;
    entry.style.removeProperty('display');
    entry.href = projection.href;
    entry.setAttribute('aria-label', projection.label);
    entry.setAttribute('data-gtender-workspace-target', projection.kind);
    entry.setAttribute('data-gtender-entry-kind', projection.kind);
    entry.removeAttribute('aria-busy');
    if (text) text.textContent = projection.label;
    if (mark) mark.textContent = projection.mark;

    root.setAttribute('data-gtender-context-target', projection.kind);
    root.setAttribute('data-gtender-context-state', 'ready');
    root.setAttribute('data-gtender-context-version', VERSION);
    root.setAttribute('data-gtender-context-ready', 'true');
    normalizeProductTitle(root);
    syncStarControl(root);
  }

  const mobileToolsQuery = window.matchMedia('(max-width: 767px)');

  function mobileToolsContext() {
    const header = document.querySelector(
      '[data-context-bar][data-context-product="gtender"][data-gtender-surface="public"]'
    );
    const tools = document.querySelector('[data-gtender-mobile-tools]');
    const sidebar = tools && tools.closest('[data-littleg-app-sidebar]');
    const account = header && header.querySelector('[data-gtender-native-auth-open]');
    const bookmarks = header && header.querySelector('[data-gtender-header-bookmarks]');
    const launcher = document.querySelector('[data-sidebar-launcher="mobile"]');

    return { header, tools, sidebar, account, bookmarks, launcher };
  }

  function syncMobileTools() {
    const context = mobileToolsContext();
    if (!context.header || !context.tools) return;

    const ready = Boolean(
      context.sidebar &&
      context.account &&
      context.bookmarks &&
      context.launcher &&
      context.bookmarks.getAttribute('data-gtender-star-list-bound') === 'true' &&
      document.querySelector('[data-gtender-auth-sheet]')
    );

    context.header.toggleAttribute('data-gtender-mobile-tools-ready', ready);
    context.tools.hidden = !ready;
  }

  document.addEventListener('click', (event) => {
    if (!mobileToolsQuery.matches || !(event.target instanceof Element)) return;

    const action = event.target.closest('[data-gtender-mobile-action]');
    if (!action) return;

    const context = mobileToolsContext();
    if (
      !context.header ||
      !context.tools ||
      !context.tools.contains(action) ||
      !context.header.hasAttribute('data-gtender-mobile-tools-ready')
    )
      return;

    const original =
      action.getAttribute('data-gtender-mobile-action') === 'account'
        ? context.account
        : context.bookmarks;

    if (!original || original.disabled) return;

    event.preventDefault();
    event.stopPropagation();

    document.dispatchEvent(
      new CustomEvent('littleg:overlay:close-others', {
        detail: { except: 'gtender-dialog' },
      })
    );

    context.launcher.focus({ preventScroll: true });
    original.click();
  });

  const mobileToolsObserver = new MutationObserver(syncMobileTools);
  mobileToolsObserver.observe(document.documentElement, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['data-gtender-star-list-bound'],
  });

  function mount(root) {
    if (!root || mounted.has(root)) return;
    mounted.add(root);
    project(root);
  }

  function boot(scope) {
    syncMobileTools();
    list(scope || document, ROOT_SELECTOR).forEach(function (root) {
      if (mounted.has(root)) project(root);
      else mount(root);
    });
  }

  function destroyAll(scope) {
    list(scope || document, ROOT_SELECTOR).forEach(function (root) {
      mounted.delete(root);
      root.removeAttribute('data-gtender-context-ready');
      root.removeAttribute('data-gtender-context-state');
      root.removeAttribute('data-gtender-context-target');
      root.removeAttribute('data-gtender-context-version');
    });
  }

  document.addEventListener('shopify:section:load', function (event) {
    boot(event.target || document);
  });
  document.addEventListener('shopify:section:unload', function (event) {
    destroyAll(event.target || document);
  });
  window.addEventListener('pageshow', function () {
    boot(document);
  });

  var api = Object.freeze({ version: VERSION, boot: boot, destroy: destroyAll });
  window[GLOBAL_KEY] = api;
  window.GTenderContextBar = api;

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      function () {
        boot(document);
      },
      { once: true }
    );
  } else {
    boot(document);
  }
})();
